import { defineStore } from 'pinia';
import { ref, watch, computed } from 'vue';
import { useFormStore } from '@/features/blueprint/stores/useFormStore';
import { evaluateRecommendationRules } from '../composables/useRecommendation';

export const usePlanSelectionStore = defineStore('planSelection', () => {
    const formStore = useFormStore();

    // 1) Base state
    const selectionToggles = ref<Record<string, boolean>>({});
    const autoSelectedPlans = ref<string[]>([]);
    const urlPreselectedPlans = ref<string[]>([]);
    const seenPlans = ref<Set<string>>(new Set());

    // 2) Load persisted toggles
    const saved = localStorage.getItem('planSelection.selectionToggles');
    if (saved) {
        selectionToggles.value = JSON.parse(saved);
    }

    // 3) **Bootstrap** new recommendations on init
    //    (runs before any watch, so first‐load always gets auto‐selected)
    {
        const { selectedPlanIds } = evaluateRecommendationRules(formStore.answers);
        autoSelectedPlans.value = selectedPlanIds;
        for (const planId of selectedPlanIds) {
            // if user hasn't explicitly toggled yet, default to "on"
            if (selectionToggles.value[planId] === undefined) {
                selectionToggles.value[planId] = true;
            }
            seenPlans.value.add(planId);
        }
    }

    // 4) Persist toggles whenever they change
    watch(
        selectionToggles, (val) => {
            localStorage.setItem(
                'planSelection.selectionToggles',
                JSON.stringify(val)
            );
        },
        { deep: true }
    );

    // 5) Recompute on answer changes (to catch new rules + new toggles)
    watch(
        () => JSON.stringify(formStore.answers),
        (newAnswersJson, oldAnswersJson) => {
            // Only recompute if answers actually changed
            if (newAnswersJson === oldAnswersJson) return;

            const { selectedPlanIds } = evaluateRecommendationRules(formStore.answers);
            autoSelectedPlans.value = selectedPlanIds;

            for (const planId of selectedPlanIds) {
                const isNew = !seenPlans.value.has(planId);
                if (isNew && selectionToggles.value[planId] === undefined) {
                    selectionToggles.value[planId] = true;
                }
                seenPlans.value.add(planId);
            }
        },
        { immediate: true } // Changed to immediate to catch initial form data loads
    );

    // 6) Build the final selection set
    const selectedPlans = computed(() => {
        const result = new Set<string>();

        // always include any auto-selected plans so long as they're not explicitly turned off
        for (const planId of autoSelectedPlans.value) {
            if (selectionToggles.value[planId] !== false) {
                result.add(planId);
            }
        }

        // include URL preselected plans so long as they're not explicitly turned off
        for (const planId of urlPreselectedPlans.value) {
            if (selectionToggles.value[planId] !== false) {
                result.add(planId);
            }
        }

        // then apply all manual toggles
        for (const [id, isOn] of Object.entries(selectionToggles.value)) {
            if (isOn) result.add(id);
            else result.delete(id);
        }

        return Array.from(result);
    });

    function togglePlanSelection(planId: string) {
        selectionToggles.value[planId] = !selectionToggles.value[planId];
    }

    function setUrlPreselectedPlans(planIds: string[]) {
        urlPreselectedPlans.value = planIds;
        
        // Clear auto-selected plans when URL plans are provided
        // This prevents mixing default recommendations with URL-specified plans
        autoSelectedPlans.value = [];
        
        // Clear existing toggles to start fresh with URL plans
        selectionToggles.value = {};
        
        // Auto-select URL preselected plans
        for (const planId of planIds) {
            selectionToggles.value[planId] = true;
            seenPlans.value.add(planId);
        }
    }

    return {
        selectionToggles,
        autoSelectedPlans,
        urlPreselectedPlans,
        selectedPlans,
        togglePlanSelection,
        setUrlPreselectedPlans,
    };
});
